import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BookOpen, Sparkles, BarChart3, ArrowLeft } from "lucide-react";
import api from "@/lib/api";

export default function StudentHome() {
  const { id } = useParams();
  const nav = useNavigate();
  const [student, setStudent] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/students/${id}`);
      setStudent(r.data);
    } catch {
      nav("/dashboard");
    }
  }, [id, nav]);

  useEffect(() => {
    load();
  }, [load]);

  if (!student) return null;

  return (
    <main className="min-h-screen" style={{ background: "var(--tm-bg-2)" }} data-testid="student-home-page">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-8">
        <Link to={`/students/${id}`} className="tm-btn tm-btn-ghost mb-6" data-testid="student-home-back">
          <ArrowLeft size={18} /> Back
        </Link>
        <div className="tm-card mb-8 flex items-center gap-5">
          <div
            className="rounded-full grid place-items-center text-white font-black text-3xl"
            style={{
              width: 84, height: 84, background: student.avatar_color || "var(--tm-brand)",
              fontFamily: "Nunito, sans-serif",
            }}
          >
            {student.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <p className="text-[color:var(--tm-ink-3)]">Hi</p>
            <h1 className="text-4xl sm:text-5xl font-black" style={{ fontFamily: "Nunito, sans-serif" }}>
              {student.name}
            </h1>
            <p className="text-lg text-[color:var(--tm-ink-2)]">Pick something to do.</p>
          </div>
        </div>

        <div className="grid gap-5" data-testid="student-home-actions">
          <BigTile
            to={`/students/${id}/homework`}
            title="Do homework together"
            desc="Bring a question. I will help you one step at a time."
            Icon={BookOpen}
            testId="student-home-homework"
          />
          <BigTile
            to={`/students/${id}/learn`}
            title="Learn something new"
            desc="Short lessons about things you like."
            Icon={Sparkles}
            testId="student-home-learn"
          />
          <BigTile
            to={`/students/${id}/progress`}
            title="See what I've done"
            desc="Streaks, badges, and things I finished."
            Icon={BarChart3}
            testId="student-home-progress"
          />
        </div>
      </div>
    </main>
  );
}

function BigTile({ to, title, desc, Icon, testId }) {
  return (
    <Link
      to={to}
      className="tm-card flex items-center gap-5 hover:border-[color:var(--tm-brand)] transition-colors"
      data-testid={testId}
    >
      <div
        className="rounded-2xl grid place-items-center flex-shrink-0"
        style={{ width: 72, height: 72, background: "var(--tm-bg-3)", color: "var(--tm-brand)" }}
      >
        <Icon size={32} strokeWidth={2.2} />
      </div>
      <div>
        <h2 className="text-2xl font-bold" style={{ fontFamily: "Nunito, sans-serif" }}>{title}</h2>
        <p className="text-lg text-[color:var(--tm-ink-3)]">{desc}</p>
      </div>
    </Link>
  );
}
